import { defaultFormData, labels } from "./ruleEngine.constants";

/**
 * Transforms form data into API payload format for creating email rules
 * @author Sarthak Arora
 */
export const transformFormDataToApiPayload = (formData, workflowId, user) => {
  const conditions = [];

  // Process locations and conditions
  Object.entries(formData.locations).forEach(([location, isSelected]) => {
    if (isSelected) {
      // Add "contains" condition if keywords exist
      if (formData.conditions[0].keywords.length > 0) {
        conditions.push({
          operation: "include",
          operator: formData.conditions[0].logic.toLowerCase(),
          location,
          phrase: formData.conditions[0].keywords,
          isDisabled: formData.conditions[0].isDisabled ? 1 : 0,
        });
      }

      // Add "does not contain" condition if keywords exist
      if (formData.conditions[1].keywords.length > 0) {
        conditions.push({
          operation: "exclude",
          operator: formData.conditions[1].logic.toLowerCase(),
          location,
          phrase: formData.conditions[1].keywords,
          isDisabled: formData.conditions[1].isDisabled ? 1 : 0,
        });
      }
    }
  });

  return {
    name: formData.name,
    workflowId: parseInt(workflowId),
    user: user.id,
    actions: formData.actions,
    conditions: conditions,
  };
};

/**
 * Transforms API response data into form data format
 * @author Sarthak Arora
 */
export const transformApiResponseToFormData = (apiData) => {
  if (!apiData) return defaultFormData;

  const locations = {
    sender: false,
    subject: false,
    body: false,
  };

  const containsConditions = [];
  const doesNotContainConditions = [];

  apiData.conditions?.forEach((condition) => {
    if (!condition.isDisabled) locations[condition.location] = true;

    if (condition.operation === "include") {
      containsConditions.push({
        id: condition.id,
        keywords: condition.phrases,
        logic: condition.operator,
        isDisabled: condition.isDisabled ? 1 : 0,
      });
    } else if (condition.operation === "exclude") {
      doesNotContainConditions.push({
        id: condition.id,
        keywords: condition.phrases,
        logic: condition.operator,
        isDisabled: condition.isDisabled ? 1 : 0,
      });
    }
  });

  return {
    name: apiData.name,
    locations,
    conditions: [
      {
        type: "contains",
        keywords: containsConditions.find((condition) => !condition.isDisabled)?.keywords || [],
        logic: containsConditions.find((condition) => !condition.isDisabled)?.logic || "and",
        isDisabled: containsConditions.find((condition) => !condition.isDisabled) ? 0 : 1,
      },
      {
        type: "does_not_contain",
        keywords: doesNotContainConditions.find((condition) => !condition.isDisabled)?.keywords || [],
        logic: doesNotContainConditions.find((condition) => !condition.isDisabled)?.logic || "or",
        isDisabled: doesNotContainConditions.find((condition) => !condition.isDisabled) ? 0 : 1,
      },
    ],
    actions: apiData.actions,
    isDisabled: apiData.isDisabled,
  };
};

/**
 * Transforms form conditions to API conditions format, identifying changes for API updates
 * @author Sarthak Arora
 */
export const prepareConditionsForUpdate = (formData, existingConditions = []) => {
  const conditions = [];
  const existingConditionMap = new Map();

  // Create a map of existing conditions
  existingConditions.forEach((condition) => {
    const key = `${condition.operation}-${condition.location}`;
    existingConditionMap.set(key, condition);
  });

  // Process locations and conditions
  Object.entries(formData.locations).forEach(([location, isSelected]) => {
    if (isSelected) {
      // Handle "contains" condition
      if (formData.conditions[0].keywords.length > 0) {
        const condition = {
          operation: "include",
          operator: formData.conditions[0].logic.toLowerCase(),
          location,
          phrases: formData.conditions[0].keywords,
          isDisabled: formData.conditions[0].isDisabled ? 1 : 0,
        };

        const key = `${condition.operation}-${location}`;
        const existingCondition = existingConditionMap.get(key);

        if (existingCondition) {
          const hasChanged =
            existingCondition.operator !== condition.operator ||
            JSON.stringify(existingCondition.phrases) !== JSON.stringify(condition.phrases) ||
            existingCondition.isDisabled !== condition.isDisabled;

          if (hasChanged) {
            // Include ID for existing condition
            condition.id = existingCondition.id;
            conditions.push(condition);
          }
        } else {
          // New condition, don't include ID
          conditions.push(condition);
        }
      }

      // Handle "does not contain" condition
      if (formData.conditions[1].keywords.length > 0) {
        const condition = {
          operation: "exclude",
          operator: formData.conditions[1].logic.toLowerCase(),
          location,
          phrases: formData.conditions[1].keywords,
          isDisabled: formData.conditions[1].isDisabled ? 1 : 0,
        };

        const key = `${condition.operation}-${location}`;
        const existingCondition = existingConditionMap.get(key);

        if (existingCondition) {
          const hasChanged =
            existingCondition.operator !== condition.operator ||
            JSON.stringify(existingCondition.phrases) !== JSON.stringify(condition.phrases) ||
            existingCondition.isDisabled !== condition.isDisabled;

          if (hasChanged) {
            // Include ID for existing condition
            condition.id = existingCondition.id;
            conditions.push(condition);
          }
        } else {
          // New condition, don't include ID
          conditions.push(condition);
        }
      }
    }
  });

  existingConditions.forEach((condition) => {
    if (!formData.locations[condition.location]) {
      conditions.push({
        id: condition.id,
        phrases: condition.phrases,
        isDisabled: 1,
      });
    }
  });
  return conditions;
};

/**
 * Validates the form data and returns validation errors if any
 * @param {Object} formData - The form data to validate
 * @returns {Object} An object containing validation errors, empty if valid
 * @author Sarthak Arora
 */
export const validateForm = (formData) => {
  const errors = {};

  if (!formData.name.trim()) {
    errors.name = labels.VALIDATION.NAME_REQUIRED;
  }

  if (!Object.values(formData.locations).some((val) => val)) {
    errors.locations = labels.VALIDATION.LOCATION_REQUIRED;
  }

  const hasEnabledCondition = formData.conditions.some((condition) => !condition.isDisabled);

  if (!hasEnabledCondition) {
    errors.conditions = labels.VALIDATION.CONDITION_REQUIRED;
  }

  const noKeywords = formData.conditions.some((condition) => !condition.isDisabled && condition.keywords.length === 0);

  if (noKeywords) {
    errors.conditions = labels.VALIDATION.KEYWORDS_REQUIRED;
  }

  // Validate actions
  if (!formData.actions.trim()) {
    errors.actions = labels.VALIDATION.ACTION_REQUIRED;
  }

  return errors;
};
